import React from 'react';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Plus } from 'lucide-react';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';

/**
 * @typedef {Object} Technician
 * @property {*} id
 * @property {*} name
 * @property {*} specialization
 * @property {*} status
 * @property {*} activeRepairs
 * @property {*} completedRepairs
 * @property {*} rating
 * @property {*} availability
 */

// Mock technicians data
const mockTechnicians = [
  {
    id: 'TECH001',
    name: 'Mike Wilson',
    specialization: 'Furniture Assembly',
    status: 'active',
    activeRepairs: 5,
    completedRepairs: 128,
    rating: 4.8,
    availability: 'available',
    location: 'Main Workshop',
    email: 'mike.wilson@ikea.com',
    phone: '+1 (555) 123-4567'
  },
  {
    id: 'TECH002',
    name: 'Sarah Brown',
    specialization: 'Upholstery',
    status: 'active',
    activeRepairs: 3,
    completedRepairs: 95,
    rating: 4.9,
    availability: 'busy',
    location: 'East Workshop',
    email: 'sarah.brown@ikea.com',
    phone: '+1 (555) 234-5678'
  },
  {
    id: 'TECH003',
    name: 'Tom Davis',
    specialization: 'Wood Repair',
    status: 'on_leave',
    activeRepairs: 0,
    completedRepairs: 156,
    rating: 4.7,
    availability: 'unavailable',
    location: 'West Workshop',
    email: 'tom.davis@ikea.com',
    phone: '+1 (555) 345-6789'
  }
];

const availabilityStatuses = [
  { value: 'all', label: 'All Statuses' },
  { value: 'available', label: 'Available' },
  { value: 'busy', label: 'Busy' },
  { value: 'unavailable', label: 'Unavailable' },
];

const specializations = [
  { value: 'all', label: 'All Specializations' },
  { value: 'Furniture Assembly', label: 'Furniture Assembly' },
  { value: 'Upholstery', label: 'Upholstery' },
  { value: 'Wood Repair', label: 'Wood Repair' },
];

const columns = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.email}</div>
      </div>
    ),
  },
  {
    accessorKey: 'specialization',
    header: 'Specialization',
    cell: ({ row }) => row.original.specialization,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status.toLowerCase();
      const variant = {
        active: 'default',
        on_leave: 'secondary',
        inactive: 'destructive'
      }[status] || 'outline';

      return (
        <Badge variant={variant}>
          {row.original.status.replace('_', ' ').toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'availability',
    header: 'Availability',
    cell: ({ row }) => {
      const availability = row.original.availability.toLowerCase();
      const variant = {
        available: 'default',
        busy: 'secondary',
        unavailable: 'destructive'
      }[availability] || 'outline';

      return (
        <Badge variant={variant}>
          {row.original.availability.toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'activeRepairs',
    header: 'Active Repairs',
    cell: ({ row }) => row.original.activeRepairs,
  },
  {
    accessorKey: 'completedRepairs',
    header: 'Completed',
    cell: ({ row }) => row.original.completedRepairs,
  },
  {
    accessorKey: 'rating',
    header: 'Rating',
    cell: ({ row }) => (
      <div className="flex items-center">
        <span className="text-yellow-500 mr-1">★</span>
        {row.original.rating.toFixed(1)}
      </div>
    ),
  },
  {
    accessorKey: 'location',
    header: 'Location',
    cell: ({ row }) => row.original.location,
  },
  {
    accessorKey: 'actions',
    header: '',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>View Profile</DropdownMenuItem>
          <DropdownMenuItem>Edit Details</DropdownMenuItem>
          <DropdownMenuItem>View Schedule</DropdownMenuItem>
          <DropdownMenuItem>Assign Repair</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export function RepairTechniciansPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    availability: 'all',
    specialization: 'all',
  });

  // Filter technicians based on current filters
  const filteredTechnicians = React.useMemo(() => {
    return mockTechnicians.filter(technician => {
      const matchesSearch =
        technician.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        technician.email.toLowerCase().includes(filters.search.toLowerCase()) ||
        technician.specialization.toLowerCase().includes(filters.search.toLowerCase());

      const matchesAvailability =
        filters.availability === 'all' ||
        technician.availability === filters.availability;

      const matchesSpecialization =
        filters.specialization === 'all' ||
        technician.specialization === filters.specialization;

      return matchesSearch && matchesAvailability && matchesSpecialization;
    });
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Repair Technicians</h1>
          <p className="text-sm text-muted-foreground">Manage repair technicians and their assignments</p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Technician
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search technicians..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.availability}
                onValueChange={(value) => handleFilterChange('availability', value)}
              >
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Filter by availability" />
                </SelectTrigger>
                <SelectContent>
                  {availabilityStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.specialization}
                onValueChange={(value) => handleFilterChange('specialization', value)}
              >
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Filter by specialization" />
                </SelectTrigger>
                <SelectContent>
                  {specializations.map((spec) => (
                    <SelectItem key={spec.value} value={spec.value}>
                      {spec.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={filteredTechnicians}
            emptyMessage="No technicians found"
            loadingMessage="Loading technicians..."
          />
        </CardContent>
      </Card>
    </div>
  );
}
